import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import {
  createDelegateElevateItem,
  deleteDelegateElevateItem,
  listDelegateElevate,
  updateDelegateElevateItem,
  type DelegateElevateItem,
  type DelegateQuadrant,
} from '@/api/eos/delegateElevate.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { describeEosError } from '@/lib/eos/errors';
import { useToast } from '@/hooks/useToast';

/**
 * EOS Delegate & Elevate — a PERSONAL 2×2 audit (ADR-043).
 *
 * The member lists their activities and buckets each into one of four
 * energy × proficiency quadrants. The high-energy quadrants are their
 * zone (do more); the low-energy quadrants are delegate/elevate targets.
 * Every operation is on the caller's OWN list — no owner picker here.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

/** Reading order of the grid: high-energy row first, then low-energy row. */
const QUADRANT_ORDER: DelegateQuadrant[] = ['love_great', 'like_good', 'dislike_good', 'dislike_notgood'];

interface QuadrantMeta {
  label: string;
  hint: string;
  /** Card border + subtle background tint — the quadrant's visual tone. */
  cardCls: string;
  /** Quadrant title colour. */
  headerCls: string;
  /** Small accent dot next to the title. */
  dotCls: string;
}

const QUADRANT_META: Record<DelegateQuadrant, QuadrantMeta> = {
  love_great: {
    label: 'Love it / Great at it',
    hint: 'Your zone — do more of this.',
    cardCls: 'border-kw-emerald/40 bg-kw-emerald-light',
    headerCls: 'text-kw-green',
    dotCls: 'bg-kw-emerald',
  },
  like_good: {
    label: 'Like it / Good at it',
    hint: 'Keep — solid and sustainable.',
    cardCls: 'border-kw-cerulean/30 bg-kw-cerulean-light',
    headerCls: 'text-kw-cerulean',
    dotCls: 'bg-kw-cerulean',
  },
  dislike_good: {
    label: "Don't like it / Good at it",
    hint: 'Delegate candidate — hand it off.',
    cardCls: 'border-kw-warning-text/25 bg-kw-warning-bg',
    headerCls: 'text-kw-warning-text',
    dotCls: 'bg-kw-warning-text',
  },
  dislike_notgood: {
    label: "Don't like it / Not good at it",
    hint: 'Delegate or elevate out — off your plate.',
    cardCls: 'border-kw-danger-text/25 bg-kw-danger-bg',
    headerCls: 'text-kw-danger-text',
    dotCls: 'bg-kw-danger-text',
  },
};

export function DelegateElevatePage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [addQuadrant, setAddQuadrant] = useState<DelegateQuadrant>('love_great');

  const itemsQuery = useQuery({
    queryKey: ['eos', 'delegate-elevate'],
    queryFn: listDelegateElevate,
  });

  const byQuadrant = useMemo(() => {
    const map: Record<DelegateQuadrant, DelegateElevateItem[]> = {
      love_great: [],
      like_good: [],
      dislike_good: [],
      dislike_notgood: [],
    };
    for (const item of itemsQuery.data ?? []) map[item.quadrant].push(item);
    for (const q of QUADRANT_ORDER) map[q].sort((a, b) => a.order - b.order);
    return map;
  }, [itemsQuery.data]);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'delegate-elevate'] });
  }

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      activity,
      quadrant,
    }: {
      id: string;
      activity?: string;
      quadrant?: DelegateQuadrant;
    }) =>
      updateDelegateElevateItem(id, {
        ...(activity !== undefined ? { activity } : {}),
        ...(quadrant !== undefined ? { quadrant } : {}),
      }),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not update the activity.'), { variant: 'danger' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDelegateElevateItem(id),
    onSuccess: () => {
      invalidate();
      toast.show('Activity removed', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not remove the activity.'), { variant: 'danger' });
    },
  });

  function openAdd(quadrant: DelegateQuadrant): void {
    setAddQuadrant(quadrant);
    setAddOpen(true);
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Delegate &amp; Elevate</h1>
          <p className="mt-1 max-w-2xl text-[14px] text-kw-muted">
            List everything you do, then bucket each activity by how much you enjoy it and how good you are at
            it. Spend more time in the top-left; delegate or elevate out of the bottom.
          </p>
        </div>
        <Button
          onClick={() => {
            openAdd('love_great');
          }}
        >
          <Plus className="size-4" />
          Add activity
        </Button>
      </div>

      {itemsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : itemsQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load your list</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {describeEosError(itemsQuery.error, 'Please refresh and try again.')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {QUADRANT_ORDER.map((quadrant) => {
            const meta = QUADRANT_META[quadrant];
            const items = byQuadrant[quadrant];
            return (
              <Card key={quadrant} className={`flex flex-col ${meta.cardCls}`}>
                <div className="border-b border-kw-border/60 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`size-2 shrink-0 rounded-full ${meta.dotCls}`} />
                    <h2 className={`text-[14px] font-semibold ${meta.headerCls}`}>{meta.label}</h2>
                    <span className="ml-auto text-[12px] font-medium text-kw-muted">{items.length}</span>
                  </div>
                  <p className="mt-1 pl-4 text-[12px] text-kw-muted">{meta.hint}</p>
                </div>

                {items.length === 0 ? (
                  <p className="flex-1 px-4 py-6 text-center text-[13px] text-kw-muted">Nothing here yet.</p>
                ) : (
                  <ul className="flex-1 divide-y divide-kw-border/60">
                    {items.map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        isBusy={updateMutation.isPending || deleteMutation.isPending}
                        onSaveActivity={(activity) => {
                          updateMutation.mutate({ id: item.id, activity });
                        }}
                        onReassign={(next) => {
                          updateMutation.mutate({ id: item.id, quadrant: next });
                        }}
                        onDelete={() => {
                          deleteMutation.mutate(item.id);
                        }}
                      />
                    ))}
                  </ul>
                )}

                <div className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => {
                      openAdd(quadrant);
                    }}
                    className="flex w-full items-center justify-center gap-1.5 rounded-kw-md py-1.5 text-[13px] font-medium text-kw-muted hover:bg-white/60 hover:text-kw-shark"
                  >
                    <Plus className="size-3.5" />
                    Add here
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AddActivityModal
        key={addQuadrant}
        open={addOpen}
        defaultQuadrant={addQuadrant}
        onClose={() => {
          setAddOpen(false);
        }}
        onCreated={() => {
          invalidate();
          toast.show('Activity added', { variant: 'success' });
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Item row — inline activity edit, quadrant re-assign, delete
// ─────────────────────────────────────────────────────────────────────────

function ItemRow({
  item,
  isBusy,
  onSaveActivity,
  onReassign,
  onDelete,
}: {
  item: DelegateElevateItem;
  isBusy: boolean;
  onSaveActivity: (activity: string) => void;
  onReassign: (quadrant: DelegateQuadrant) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.activity);

  function startEdit(): void {
    setDraft(item.activity);
    setEditing(true);
  }

  function commit(): void {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== item.activity) onSaveActivity(trimmed);
    setEditing(false);
  }

  if (editing) {
    return (
      <li className="px-3 py-2.5">
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            commit();
          }}
          className="flex items-center gap-1.5"
        >
          <input
            autoFocus
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
            }}
            className="min-w-0 flex-1 rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
          <button
            type="submit"
            aria-label="Save activity"
            className="rounded p-1 text-kw-green hover:bg-white"
          >
            <Check className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Cancel edit"
            onClick={() => {
              setEditing(false);
            }}
            className="rounded p-1 text-kw-muted hover:bg-white hover:text-kw-shark"
          >
            <X className="size-4" />
          </button>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-start gap-2 px-3 py-2.5">
      <p className="min-w-0 flex-1 break-words text-[13px] text-kw-shark">{item.activity}</p>
      <div className="flex shrink-0 items-center gap-1">
        <label className="sr-only" htmlFor={`quadrant-${item.id}`}>
          Move {item.activity} to another quadrant
        </label>
        <select
          id={`quadrant-${item.id}`}
          value={item.quadrant}
          disabled={isBusy}
          onChange={(e) => {
            onReassign(e.target.value as DelegateQuadrant);
          }}
          className={SELECT_CLS}
        >
          {QUADRANT_ORDER.map((q) => (
            <option key={q} value={q}>
              {QUADRANT_META[q].label}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label={`Edit ${item.activity}`}
          onClick={startEdit}
          className="rounded p-1 text-kw-muted hover:bg-white hover:text-kw-shark"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label={`Delete ${item.activity}`}
          disabled={isBusy}
          onClick={onDelete}
          className="rounded p-1 text-kw-muted hover:bg-white hover:text-kw-danger-text disabled:opacity-40"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add activity modal (activity text + quadrant picker)
// ─────────────────────────────────────────────────────────────────────────

function AddActivityModal({
  open,
  defaultQuadrant,
  onClose,
  onCreated,
}: {
  open: boolean;
  defaultQuadrant: DelegateQuadrant;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [activity, setActivity] = useState('');
  const [quadrant, setQuadrant] = useState<DelegateQuadrant>(defaultQuadrant);

  const mutation = useMutation({
    mutationFn: () => createDelegateElevateItem({ activity: activity.trim(), quadrant }),
    onSuccess: () => {
      onCreated();
      onClose();
      setActivity('');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!activity.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add an activity" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Activity"
          name="activity"
          required
          value={activity}
          onChange={(e) => {
            setActivity(e.target.value);
          }}
          placeholder="e.g. Reviewing weekly scorecards"
        />

        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Quadrant</span>
          <select
            value={quadrant}
            onChange={(e) => {
              setQuadrant(e.target.value as DelegateQuadrant);
            }}
            className={SELECT_CLS}
          >
            {QUADRANT_ORDER.map((q) => (
              <option key={q} value={q}>
                {QUADRANT_META[q].label}
              </option>
            ))}
          </select>
          <span className="text-[12px] text-kw-muted">{QUADRANT_META[quadrant].hint}</span>
        </label>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeEosError(mutation.error, 'Could not add the activity.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!activity.trim()}>
            Add activity
          </Button>
        </div>
      </form>
    </Modal>
  );
}
